import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, UserPlus, Copy, Wrench, CheckCircle2, XCircle, Ban, Users, ShieldAlert, Power, RotateCcw, Gauge, ClipboardCheck } from "lucide-react";
import { useAuth, apiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Operator() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ email: "", founder_name: "", company_name: "" });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const [toolData, setToolData] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWs, setSelectedWs] = useState("");
  const [wsTools, setWsTools] = useState(null);
  const [users, setUsers] = useState([]);
  const [privacyReqs, setPrivacyReqs] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [ceilingEdit, setCeilingEdit] = useState({});

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/operator/users");
      setUsers(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadPrivacy = async () => {
    try {
      const { data } = await api.get("/operator/privacy-requests");
      setPrivacyReqs(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadReadiness = async () => {
    try {
      const { data } = await api.get("/operator/release-readiness");
      setReadiness(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setDisabled = async (userId, disabled) => {
    try {
      await api.post(`/operator/users/${userId}/disable`, { disabled });
      toast.success(disabled ? "Account disabled." : "Account enabled.");
      loadUsers();
    } catch (e) { toast.error(apiError(e)); }
  };

  const forceLogout = async (userId) => {
    try {
      await api.post(`/operator/users/${userId}/force-logout`, {});
      toast.success("All sessions revoked.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const forceReset = async (userId) => {
    try {
      const { data } = await api.post(`/operator/users/${userId}/force-reset`, {});
      setResetResult(data);
      toast.success("Temporary password issued.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveCeiling = async (wsId) => {
    const val = parseInt(ceilingEdit[wsId], 10);
    if (Number.isNaN(val)) return;
    try {
      await api.post(`/operator/workspaces/${wsId}/ceiling`, { monthly_model_requests: val });
      toast.success("Usage ceiling updated.");
      loadUsers();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setPrivacyStatus = async (id, status) => {
    try {
      await api.post(`/operator/privacy-requests/${id}/status`, { status });
      loadPrivacy();
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/operator/beta-requests");
      setRequests(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadTools = async () => {
    try {
      const { data } = await api.get("/operator/tools");
      setToolData(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadWorkspaces = async () => {
    try {
      const { data } = await api.get("/operator/workspaces");
      setWorkspaces(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadWsTools = async (wsId) => {
    if (!wsId) { setWsTools(null); return; }
    try {
      const { data } = await api.get(`/operator/workspaces/${wsId}/tools`);
      setWsTools(data.tools);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    load();
    loadTools();
    loadWorkspaces();
    loadUsers();
    loadPrivacy();
    loadReadiness();
  }, []);

  const toggleGlobal = async (toolId, enabled) => {
    try {
      await api.post(`/operator/tools/${toolId}/global`, { enabled });
      toast.success(`${toolId} ${enabled ? "enabled" : "disabled"} globally.`);
      loadTools();
      if (selectedWs) loadWsTools(selectedWs);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleWorkspace = async (toolId, enabled) => {
    if (!selectedWs) return;
    try {
      await api.post(`/operator/workspaces/${selectedWs}/tools/${toolId}`, { enabled });
      toast.success(`${toolId} ${enabled ? "enabled" : "disabled"} for this workspace.`);
      loadWsTools(selectedWs);
    } catch (e) { toast.error(apiError(e)); }
  };

  const onSelectWs = (wsId) => { setSelectedWs(wsId); loadWsTools(wsId); };

  const provision = async (e) => {
    e.preventDefault();
    setBusy(true);
    setIssued(null);
    try {
      const { data } = await api.post("/operator/provision", form);
      setIssued(data);
      toast.success("Workspace provisioned.");
      loadWorkspaces();
      setForm({ email: "", founder_name: "", company_name: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-wr-bg wr-grid-bg">
      <header className="border-b border-wr-border bg-black/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Wordmark size="sm" />
          <div className="flex items-center gap-4">
            <span className="wr-label hidden sm:inline">Operator Console</span>
            <Button onClick={doLogout} variant="ghost" data-testid="operator-logout" className="rounded-none text-wr-sub hover:text-wr-red wr-mono uppercase tracking-wider text-xs">
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <p className="wr-label !text-wr-red">▸ OPERATOR</p>
        <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl mt-2">Private-beta control.</h1>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Provision */}
          <section className="border border-wr-border bg-wr-surface p-5">
            <h2 className="wr-label flex items-center gap-2"><UserPlus size={14} className="text-wr-red" /> Provision workspace</h2>
            <form onSubmit={provision} className="mt-4 space-y-4" data-testid="provision-form">
              <div>
                <label className="wr-label">Founder email</label>
                <Input required type="email" data-testid="prov-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
              </div>
              <div>
                <label className="wr-label">Founder name</label>
                <Input required data-testid="prov-name" value={form.founder_name} onChange={(e) => setForm({ ...form, founder_name: e.target.value })} className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
              </div>
              <div>
                <label className="wr-label">Company</label>
                <Input data-testid="prov-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0" />
              </div>
              <Button type="submit" disabled={busy} data-testid="prov-submit" className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider">
                {busy ? "Provisioning…" : "Provision"}
              </Button>
            </form>
            {issued && (
              <div className="mt-4 border border-wr-red/40 bg-wr-redMuted p-4" data-testid="provision-result">
                <p className="wr-label !text-wr-red">Temporary credentials — share securely</p>
                <p className="wr-mono text-sm text-white mt-2 break-all">{issued.email}</p>
                <p className="wr-mono text-sm text-white break-all flex items-center gap-2">
                  {issued.temp_password}
                  <button onClick={() => { navigator.clipboard?.writeText(issued.temp_password); toast.success("Copied"); }} data-testid="copy-temp-pw">
                    <Copy size={13} className="text-wr-sub hover:text-white" />
                  </button>
                </p>
              </div>
            )}
          </section>

          {/* Beta requests */}
          <section className="border border-wr-border bg-wr-surface p-5">
            <h2 className="wr-label">Private-beta requests ({requests.length})</h2>
            <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto" data-testid="beta-requests-list">
              {requests.length === 0 && <p className="text-sm text-wr-muted">No requests yet.</p>}
              {requests.map((r) => (
                <div key={r.id} className="border border-wr-border p-3" data-testid={`beta-req-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-white truncate">{r.name}</p>
                    <span className="wr-mono text-[0.68rem] text-wr-muted">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="wr-mono text-xs text-wr-sub truncate">{r.email}</p>
                  {r.company && <p className="text-xs text-wr-muted mt-0.5">{r.company}{r.website ? ` · ${r.website}` : ""}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {r.plan_interest && (
                      <span className={`wr-mono text-[0.58rem] uppercase px-2 py-0.5 border ${r.plan_interest === "annual" ? "border-emerald-500/40 text-emerald-400" : "border-wr-red/40 text-wr-red"}`} data-testid={`beta-plan-${r.id}`}>
                        {r.plan_interest === "annual" ? "Annual $1,490" : r.plan_interest === "monthly" ? "Monthly $149" : "Undecided"}
                      </span>
                    )}
                    {r.revenue_range && <span className="wr-mono text-[0.58rem] uppercase px-2 py-0.5 border border-wr-border text-wr-muted">rev: {r.revenue_range}</span>}
                    {r.team_size && <span className="wr-mono text-[0.58rem] uppercase px-2 py-0.5 border border-wr-border text-wr-muted">team: {r.team_size}</span>}
                  </div>
                  {r.operating_constraint && <p className="text-xs text-wr-sub mt-1.5 line-clamp-3">{r.operating_constraint}</p>}
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Tool control */}
        <section className="mt-6 border border-wr-border bg-wr-surface p-5" data-testid="operator-tools">
          <h2 className="wr-label flex items-center gap-2"><Wrench size={14} className="text-wr-red" /> Tool availability</h2>
          <p className="text-xs text-wr-muted mt-1">Global enable/disable for configured tools. Not-configured tools cannot be enabled during Stage 1.</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {(toolData?.tools || []).map((t) => {
              const usage = toolData?.usage?.[t.id] || {};
              return (
                <div key={t.id} className="border border-wr-border p-3" data-testid={`op-tool-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-white">{t.name}</p>
                      <p className="wr-mono text-[0.58rem] text-wr-muted uppercase mt-0.5">v{t.version} · {t.executor} · {t.provider || "internal"}</p>
                    </div>
                    {t.configured ? (
                      <button onClick={() => toggleGlobal(t.id, !t.global_enabled)} data-testid={`op-tool-toggle-${t.id}`}
                        className={`wr-mono text-[0.6rem] uppercase px-2.5 py-1 border ${t.global_enabled ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                        {t.global_enabled ? "Enabled" : "Disabled"}
                      </button>
                    ) : (
                      <span className="wr-mono text-[0.6rem] uppercase px-2.5 py-1 border border-wr-border text-wr-muted">Not configured</span>
                    )}
                  </div>
                  <p className="wr-mono text-[0.62rem] text-wr-muted mt-2">
                    runs {usage.runs || 0} · <span className="text-emerald-400">{usage.success || 0} ok</span> · <span className="text-wr-red">{usage.failed || 0} failed</span> · <span className="text-amber-400">{usage.blocked || 0} blocked</span>
                  </p>
                </div>
              );
            })}
          </div>
          {toolData?.failed_runs?.length > 0 && (
            <div className="mt-4" data-testid="operator-failed-runs">
              <p className="wr-label">Recent failed / blocked runs</p>
              <div className="mt-2 space-y-1">
                {toolData.failed_runs.slice(0, 12).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs border-b border-wr-border py-1">
                    {r.status === "failed" ? <XCircle size={12} className="text-wr-red" /> : <Ban size={12} className="text-amber-400" />}
                    <span className="text-white">{r.tool_id}</span>
                    <span className="wr-mono text-[0.6rem] text-wr-muted truncate">{r.error}</span>
                    <span className="wr-mono text-[0.6rem] text-wr-muted ml-auto">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-workspace entitlements */}
          <div className="mt-6 border-t border-wr-border pt-5" data-testid="operator-ws-tools">
            <p className="wr-label">Per-workspace tool access</p>
            <p className="text-xs text-wr-muted mt-1">Enable/disable configured tools for a specific workspace. Not-configured tools stay locked.</p>
            <select
              data-testid="ws-select"
              value={selectedWs}
              onChange={(e) => onSelectWs(e.target.value)}
              className="mt-3 w-full sm:w-96 bg-wr-bg border border-wr-border text-white text-sm px-3 py-2.5 rounded-none focus:outline-none"
            >
              <option value="">Select a workspace…</option>
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>{w.name} — {w.founder_email || "no founder"}</option>
              ))}
            </select>
            {wsTools && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {wsTools.map((t) => (
                  <div key={t.id} className="border border-wr-border p-3 flex items-center justify-between gap-3" data-testid={`ws-tool-${t.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-white">{t.name}</p>
                      <p className="wr-mono text-[0.58rem] text-wr-muted mt-0.5">{t.status_reason || "Available"}</p>
                    </div>
                    {t.configured ? (
                      <button onClick={() => toggleWorkspace(t.id, !t.enabled)} data-testid={`ws-tool-toggle-${t.id}`}
                        className={`wr-mono text-[0.6rem] uppercase px-2.5 py-1 border shrink-0 ${t.enabled ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                        {t.enabled ? "Enabled" : "Disabled"}
                      </button>
                    ) : (
                      <span className="wr-mono text-[0.6rem] uppercase px-2.5 py-1 border border-wr-border text-wr-muted shrink-0">Not configured</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Accounts */}
        <section className="mt-6 border border-wr-border bg-wr-surface p-5" data-testid="operator-accounts">
          <h2 className="wr-label flex items-center gap-2"><Users size={14} className="text-wr-red" /> Founder accounts ({users.length})</h2>
          <p className="text-xs text-wr-muted mt-1">Disable/enable access, revoke sessions, issue a temporary password, and set the monthly AI usage ceiling.</p>
          {resetResult && (
            <div className="mt-4 border border-wr-red/40 bg-wr-redMuted p-4" data-testid="reset-result">
              <p className="wr-label !text-wr-red">Temporary password — share securely</p>
              <p className="wr-mono text-sm text-white mt-2 break-all">{resetResult.email}</p>
              <p className="wr-mono text-sm text-white break-all flex items-center gap-2">
                {resetResult.temp_password}
                <button onClick={() => { navigator.clipboard?.writeText(resetResult.temp_password); toast.success("Copied"); }} data-testid="copy-reset-pw">
                  <Copy size={13} className="text-wr-sub hover:text-white" />
                </button>
              </p>
            </div>
          )}
          <div className="mt-4 space-y-3" data-testid="accounts-list">
            {users.length === 0 && <p className="text-sm text-wr-muted">No founder accounts yet.</p>}
            {users.map((u) => (
              <div key={u.id} className="border border-wr-border p-3" data-testid={`account-${u.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{u.name || u.email}</p>
                    <p className="wr-mono text-xs text-wr-sub truncate">{u.email}</p>
                    <p className="text-[0.68rem] text-wr-muted mt-0.5">{u.workspace?.name || "no workspace"}</p>
                  </div>
                  <span className={`wr-mono text-[0.6rem] uppercase px-2 py-1 border ${u.disabled ? "border-wr-red/40 text-wr-red" : "border-emerald-500/40 text-emerald-400"}`} data-testid={`account-status-${u.id}`}>
                    {u.disabled ? "Disabled" : "Active"}
                  </span>
                </div>
                {u.usage && (
                  <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                    <Gauge size={12} className="text-wr-muted" />
                    <span className="wr-mono text-[0.62rem] text-wr-muted">{u.usage.used}/{u.usage.limit} this month</span>
                    {u.workspace && (
                      <>
                        <Input
                          data-testid={`ceiling-input-${u.id}`}
                          defaultValue={u.usage.limit}
                          onChange={(e) => setCeilingEdit({ ...ceilingEdit, [u.workspace.id]: e.target.value })}
                          className="h-7 w-24 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 text-xs"
                        />
                        <button onClick={() => saveCeiling(u.workspace.id)} data-testid={`ceiling-save-${u.id}`}
                          className="wr-mono text-[0.6rem] uppercase px-2 py-1 border border-wr-border text-wr-sub hover:text-white">Set cap</button>
                      </>
                    )}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => setDisabled(u.id, !u.disabled)} data-testid={`account-toggle-${u.id}`}
                    className={`wr-mono text-[0.6rem] uppercase px-2.5 py-1 border flex items-center gap-1 ${u.disabled ? "border-emerald-500/40 text-emerald-400" : "border-wr-red/40 text-wr-red"}`}>
                    <Power size={11} /> {u.disabled ? "Enable" : "Disable"}
                  </button>
                  <button onClick={() => forceLogout(u.id)} data-testid={`account-logout-${u.id}`}
                    className="wr-mono text-[0.6rem] uppercase px-2.5 py-1 border border-wr-border text-wr-sub hover:text-white flex items-center gap-1">
                    <LogOut size={11} /> Revoke sessions
                  </button>
                  <button onClick={() => forceReset(u.id)} data-testid={`account-reset-${u.id}`}
                    className="wr-mono text-[0.6rem] uppercase px-2.5 py-1 border border-wr-border text-wr-sub hover:text-white flex items-center gap-1">
                    <RotateCcw size={11} /> Force reset
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Privacy requests */}
        <section className="mt-6 border border-wr-border bg-wr-surface p-5" data-testid="operator-privacy">
          <h2 className="wr-label flex items-center gap-2"><ShieldAlert size={14} className="text-wr-red" /> Data &amp; privacy requests ({privacyReqs.length})</h2>
          <div className="mt-4 space-y-2" data-testid="privacy-req-list">
            {privacyReqs.length === 0 && <p className="text-sm text-wr-muted">No requests.</p>}
            {privacyReqs.map((r) => (
              <div key={r.id} className="border border-wr-border p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`op-privacy-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-white capitalize">{r.kind} — <span className="text-wr-sub">{r.email}</span></p>
                  <p className="text-[0.68rem] text-wr-muted">{r.workspace_name} · {new Date(r.created_at).toLocaleString()}</p>
                </div>
                <select value={r.status} onChange={(e) => setPrivacyStatus(r.id, e.target.value)} data-testid={`op-privacy-status-${r.id}`}
                  className="bg-wr-bg border border-wr-border text-white text-xs px-2 py-1.5 rounded-none focus:outline-none">
                  {["open", "in_progress", "fulfilled", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        </section>

        {/* Release readiness */}
        <section className="mt-6 border border-wr-border bg-wr-surface p-5" data-testid="operator-readiness">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="wr-label flex items-center gap-2"><ClipboardCheck size={14} className="text-wr-red" /> Release readiness</h2>
            <button onClick={loadReadiness} data-testid="readiness-refresh" className="wr-label hover:text-white">Re-check</button>
          </div>
          {readiness && (
            <>
              <p className={`wr-mono text-sm mt-3 ${readiness.ok ? "text-emerald-400" : "text-wr-red"}`} data-testid="readiness-summary">
                env: {readiness.env} · {readiness.ok ? "READY" : "NOT READY (fail-closed in production)"}
              </p>
              <p className="text-[0.68rem] text-wr-muted mt-1">Reports key names and pass/fail only — never secret values.</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {readiness.checks.map((c) => (
                  <div key={c.key} className="flex items-center gap-2 border border-wr-border p-2" data-testid={`readiness-${c.key}`}>
                    {c.ok ? <CheckCircle2 size={13} className="text-emerald-400 shrink-0" /> : <XCircle size={13} className="text-wr-red shrink-0" />}
                    <span className="wr-mono text-[0.62rem] text-white">{c.key}</span>
                    <span className="wr-mono text-[0.58rem] text-wr-muted ml-auto truncate">{c.status}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
